import "@/assets/styles/custom.scss";

import "bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "intl-tel-input/styles";

// Imported last so its `body` font-family wins over Bootstrap's system-ui default (source order).
import "@/assets/styles/global.scss";

import App from "@/App.vue";
import { createApp, type Component } from "vue";

const app = createApp(App);

const url = new URL("/", location.href);
if (import.meta.env.DEV) url.port = "8000";
app.config.globalProperties.$apiBase = url.toString().replace(/\/$/, "");

import { key, store, resetTenantState } from "@/stores";
app.use(store, key);
store.commit("setApiBase", app.config.globalProperties.$apiBase);

import router from "@/router";

import { library } from "@fortawesome/fontawesome-svg-core";
import {
  faCalendar,
  faCheckSquare,
  faCircle,
  faCircleQuestion,
  faCircleXmark,
  faFileAudio,
  faFileLines,
} from "@fortawesome/free-regular-svg-icons";
import {
  faArrowRight,
  faArrowRotateRight,
  faArrowUpFromBracket,
  faArrowUpRightFromSquare,
  faBoxArchive,
  faCaretDown,
  faCaretUp,
  faChevronLeft,
  faChevronRight,
  faCopy,
  faEllipsisVertical,
  faExternalLink,
  faFileArrowUp,
  faFilter,
  faLanguage,
  faLock,
  faMagicWandSparkles,
  faMinus,
  faPen,
  faPhone,
  faPlugCirclePlus,
  faPlus,
  faQuestion,
  faRobot,
  faRotateRight,
  faSearch,
  faSort,
  faSortDown,
  faSortUp,
  faSquareXmark,
  faThumbsDown,
  faThumbsUp,
  faTrash,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
library.add([
  faArrowUpFromBracket,
  faArrowUpRightFromSquare,
  faArrowRotateRight,
  faArrowRight,
  faBoxArchive,
  faCalendar,
  faCaretUp,
  faCaretDown,
  faCheckSquare,
  faChevronLeft,
  faChevronRight,
  faCircle,
  faCircleQuestion,
  faCircleXmark,
  faCopy,
  faFileArrowUp,
  faEllipsisVertical,
  faExternalLink,
  faFileAudio,
  faFileLines,
  faFilter,
  faLanguage,
  faLock,
  faMagicWandSparkles,
  faMinus,
  faPen,
  faPhone,
  faPlus,
  faPlugCirclePlus,
  faRobot,
  faRotateRight,
  faSearch,
  faSort,
  faSortDown,
  faSortUp,
  faSquareXmark,
  faThumbsDown,
  faThumbsUp,
  faTrash,
  faXmark,
  faQuestion,
]);

import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
// Cast to `Component`: vue-fontawesome 3.3.0 types FontAwesomeIcon as
// `DefineComponent<FontAwesomeIconProps>`, whose full prop union makes
// `app.component()` blow up with TS2590 ("union type too complex").
app.component("font-awesome-icon", FontAwesomeIcon as Component);

import * as Sentry from "@sentry/vue";

import mixpanel from "mixpanel-browser";
import {
  authenticatedApi,
  configureAuthentication,
} from "@/utils/authentication";

let analyticsInitialized = false;
const initializeAnalytics = () => {
  if (
    analyticsInitialized ||
    location.pathname === "/login" ||
    !import.meta.env.VITE_MIXPANEL_TOKEN
  )
    return;
  mixpanel.init(import.meta.env.VITE_MIXPANEL_TOKEN, {
    debug: false,
    track_pageview: true,
    persistence: "localStorage",
    record_sessions_percent: 100,
    record_heatmap_data: true,
  });
  analyticsInitialized = true;
};

const clientId = import.meta.env.VITE_WORKOS_CLIENT_ID;
if (!clientId) throw new Error("VITE_WORKOS_CLIENT_ID is required");
const developmentOrigins = [
  "http://localhost:5173",
  "https://screening-dev.blancai.io",
];
const isDevelopment = import.meta.env.VITE_RELEASE !== "true";
const redirectUri =
  import.meta.env.VITE_WORKOS_REDIRECT_URI ??
  (isDevelopment && developmentOrigins.includes(location.origin)
    ? location.origin
    : undefined);
if (!redirectUri)
  throw new Error("An approved VITE_WORKOS_REDIRECT_URI is required");
const apiHostname =
  import.meta.env.VITE_WORKOS_API_HOSTNAME ?? "api.workos.com";
let routerInstalled = false;
let pendingTarget: string | undefined;
authenticatedApi.defaults.baseURL = app.config.globalProperties.$apiBase;
const auth = configureAuthentication({
  clientId,
  redirectUri,
  logoutReturnUri: new URL("/", redirectUri).href,
  apiHostname,
  devMode: isDevelopment,
  resetTenantState,
  resetAnalytics: () => {
    if (analyticsInitialized) mixpanel.reset();
  },
  identifyAnalytics: (id) => {
    initializeAnalytics();
    if (analyticsInitialized) mixpanel.identify(id);
  },
  navigate: async (target) => {
    if (routerInstalled) await router.replace(target);
    else pendingTarget = target;
  },
  reevaluateRoutes: async () => {
    await router.replace({
      path: router.currentRoute.value.fullPath,
      force: true,
    });
  },
});

import { vAutoResize } from "@/directives/autoResize";
app.directive("auto-resize", vAutoResize);

import { setupMock } from "@/api/setupMock";
setupMock();

const bootstrap = async () => {
  await auth.initialize();
  app.use(router);
  routerInstalled = true;
  await router.isReady();
  if (pendingTarget) await router.replace(pendingTarget);
  initializeAnalytics();
  if (import.meta.env.VITE_SENTRY_DSN && location.pathname !== "/login") {
    Sentry.init({
      app,
      dsn: import.meta.env.VITE_SENTRY_DSN,
      integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration(),
      ],
    });
  }
  app.mount("#app");
};
void bootstrap();
